import type { ScreenerData } from "epsm-api-types/models";
import { useMemo } from "react";
import {
	formatValueBigNumber,
	formatValueNumber,
} from "@/components/widgets/helpers/FormattingHelpers";
import { componentClassNames } from "@/components/widgets/Quote/config";
import { formatPositivePercentValue } from "@/components/widgets/Quote/lib/helpers/formatPositivePercentValue";
import {
	formatImproving,
	formatPositive,
} from "@/components/widgets/Quote/lib/helpers/quoteValueFormatters";
import { QuoteRow } from "@/components/widgets/Quote/ui/components";
import { LineHeader } from "@/components/widgets/Quote/ui/components/LineHeader";
import { valueColor } from "@/components/widgets/Revision/utils/RevisionTableUtils";
import { cn } from "@/lib/utils";

export const KeyDataPointsTable = ({
	screenerData,
}: {
	screenerData: ScreenerData | null;
}) => {
	const {
		peg_ttm,
		peg_ftm,
		pe_ttm_ratio,
		pe_fy_ratio,
		pe_next_fy_ratio,
		pe_ftm_ratio,
		fcffgrowth,
		roe,
		roic,
		debttoequity,
		dividend_yield,
		market_cap,
		profit_margin_change_curr_and_4_q_back,
		free_cash_flow_change_curr_and_4_q_back,
		freecashflow,
	} = screenerData ?? {};

	const hasAnyData = useMemo(
		() =>
			peg_ttm != null ||
			peg_ftm != null ||
			pe_ttm_ratio != null ||
			pe_ftm_ratio != null ||
			pe_fy_ratio != null ||
			pe_next_fy_ratio != null ||
			fcffgrowth?.["0_Q"] != null ||
			roe?.["0_Q"] != null ||
			roic?.["0_Q"] != null ||
			debttoequity?.["0_Q"] != null ||
			dividend_yield != null ||
			market_cap != null ||
			profit_margin_change_curr_and_4_q_back != null ||
			free_cash_flow_change_curr_and_4_q_back != null ||
			freecashflow?.["0_Q"] != null,
		[
			peg_ttm,
			peg_ftm,
			pe_ttm_ratio,
			pe_ftm_ratio,
			pe_fy_ratio,
			pe_next_fy_ratio,
			fcffgrowth?.["0_Q"],
			roe?.["0_Q"],
			roic?.["0_Q"],
			debttoequity?.["0_Q"],
			dividend_yield,
			market_cap,
			profit_margin_change_curr_and_4_q_back,
			free_cash_flow_change_curr_and_4_q_back,
			freecashflow?.["0_Q"],
		],
	);

	if (!hasAnyData) return null;

	return (
		<div className={componentClassNames} data-testid="key-data-points-table">
			<LineHeader title="Key Data Points" />

			<div className="flex justify-between gap-4">
				{pe_ttm_ratio != null && (
					<QuoteRow left="PE TTM" right={formatValueNumber(pe_ttm_ratio)} />
				)}
				{pe_ftm_ratio != null && (
					<QuoteRow left="PE FTM" right={formatValueNumber(pe_ftm_ratio)} />
				)}
			</div>
			<div className="flex justify-between gap-4">
				{pe_fy_ratio != null && (
					<QuoteRow left="PE Fwd 1Y" right={formatValueNumber(pe_fy_ratio)} />
				)}
				{pe_next_fy_ratio != null && (
					<QuoteRow
						left="PE Fwd 2Y"
						right={formatValueNumber(pe_next_fy_ratio)}
					/>
				)}
			</div>
			<div className="flex justify-between gap-4">
				{peg_ttm != null && (
					<QuoteRow left="PEG TTM" right={formatValueNumber(peg_ttm)} />
				)}

				{peg_ftm != null && (
					<QuoteRow left="PEG FTM" right={formatValueNumber(peg_ftm)} />
				)}
			</div>

			{profit_margin_change_curr_and_4_q_back != null && (
				<QuoteRow
					left="Margins"
					right={`${formatPositivePercentValue(
						profit_margin_change_curr_and_4_q_back,
					)} ${formatImproving(profit_margin_change_curr_and_4_q_back)}`}
					className={cn(
						valueColor(profit_margin_change_curr_and_4_q_back),
						"font-bold",
					)}
				/>
			)}
			{free_cash_flow_change_curr_and_4_q_back != null && (
				<QuoteRow
					left="FCF Growth"
					right={
						<>
							<span
								className={valueColor(free_cash_flow_change_curr_and_4_q_back)}
							>
								{formatPositivePercentValue(
									free_cash_flow_change_curr_and_4_q_back,
								)}
							</span>
							{freecashflow?.["0_Q"] != null && (
								<span>
									<span className={valueColor(freecashflow["0_Q"])}>
										{formatPositive(freecashflow["0_Q"])}
									</span>
								</span>
							)}
						</>
					}
					className="flex gap-0.5 font-bold"
				/>
			)}
			{roe?.["0_Q"] != null && (
				<QuoteRow
					left="ROE"
					right={`${formatPositivePercentValue(roe["0_Q"])} ${formatImproving(roe["0_Q"])}`}
					className={cn(valueColor(roe["0_Q"]), "font-bold")}
				/>
			)}
			{roic?.["0_Q"] != null && (
				<QuoteRow left="ROIC" right={formatPositivePercentValue(roic["0_Q"])} />
			)}
			{debttoequity?.["0_Q"] != null && (
				<QuoteRow
					left="Debt to Equity"
					right={formatValueNumber(debttoequity["0_Q"])}
				/>
			)}
			{dividend_yield != null && (
				<QuoteRow
					left="Div Yield"
					right={formatPositivePercentValue(dividend_yield)}
				/>
			)}
			{market_cap != null && (
				<QuoteRow left="Market Cap" right={formatValueBigNumber(market_cap)} />
			)}
		</div>
	);
};
